<template>
    <div class="grid-layout">
        <div class="u-bg--white u-align--left">
            <top :theme="theme" :class="{orange: theme === 'orange', blue: theme == 'blue'}" :titleText="titleText" :nameText="nameText"/>
        </div>
        <div class="u-bg--grey">

            <!--
            The result of the query will be available as variables inside this tag.
            So if the query is success, the result will be available in the var 'data'.

            Note: the variable 'lastCursor' is specific to the gridlayout & pagination, that's
            why it has not to be defined by the parent component.
            -->
            <ApolloQuery :query="query"
                         :variables="{
                   ...variables,
                    pageSize: pageSize,
                    lastCursor: lastCursor,
                    pathologySlug: currentPathologySlug
                     }"
            >
                <template slot-scope="{ result: { error, data, loading }, isLoading }">

                    <row class="grid-top">
                        <!-- Filters -->
                        <div class="col-lg-7 col-xs-12 filters">
                            <router-link :to="{params: { pathologie: undefined }}">
                                <div class="all"
                                     @click="setAllActive()" :class="{active: allActive, orange: theme === 'orange', blue: theme == 'blue'}"
                                >
                                    Toutes
                                </div>
                            </router-link>
                            <dropdown
                                      :theme="theme" :class="{orange: theme === 'orange', blue: theme == 'blue', selected: selectedPathology}" :selectedPathology="selectedPathology"
                                      @pathology-selected="pathologySelectionListener"
                            />
                        </div>

                        <!-- Pagination top -->
                        <div class="col-5">
                            <!-- displayed only if query succeeded -->
                            <div v-if="loading" class="loading"></div>
                            <div v-else-if="data">
                                <pagination
                                        :page-size="pageSize"
                                        :nb-results="data[type].totalCount"
                                        :current-page="currentPage"
                                        :class="{orange: theme === 'orange', blue: theme == 'blue'}"
                                        :theme="theme"
                                />
                            </div>
                        </div>
                    </row>

                    <!--
                    Cards
                    Note: the slot "main-section" must have been defined by the calling template.
                    -->
                    <div v-if="isLoading"><loading/></div>
                    <div v-if="error"><error/></div>
                    <row class="card-list" v-if="data[type].totalCount">

                        <slot name="main-section" :data="data"></slot>
                    </row>

                    <div v-else>
                        <div class="col-lg-12">Aucun résultat</div>
                    </div>

                    <!-- Pagination bottom -->
                    <!-- displayed only if query succeeded -->
                    <div v-if="loading" class="loading"></div>
                    <div v-else-if="data">
                        <div class="pagination-bottom">
                            <pagination
                                    :page-size="pageSize"
                                    :nb-results="data[type].totalCount"
                                    :current-page="currentPage"
                                    :class="{orange: theme === 'orange', blue: theme == 'blue'}"
                            />
                        </div>
                    </div>
                </template>
            </ApolloQuery>

        </div>
    </div>
</template>

<script>
  import Top from "../../components/Core/Top";
  import Row from "../../components/Layout/Row";
  import Dropdown from "../../components/Core/Dropdown";
  import Pagination from "../../components/Core/Pagination";
  import Loading from "../Core/Loading";
  import Error from "../Core/Error";


  /*
    Props:
    - query: reference to graphql query
    - variables: variables used in the graphql query
    - theme: css color
    - type: graphql node type
    - titleText: the title to display
    - pageSize: nb of items per page
   */
  export default {
    name: "GridLayout",
    components: {Error, Loading, Pagination, Dropdown, Row, Top},
    props: ['query', 'variables', 'theme', 'type', 'titleText', 'nameText', 'pageSize'],
    computed: {
      lastCursor() {
        var lastCursor = '';
        // for page <= 1, no cursor
        if (this.currentPage >= 1) {
          // index of the last item of the previous page
          var index = (this.currentPage - 1) * this.pageSize - 1;
          // DIRTY TRICK !!! Pour l'instant api-platform ne supporte pas le parametre 'offset'
          // Il ne supporte que le 'after'. Et pour cela il calcule le cursor = base64_encode (cursor).
          lastCursor = btoa(index);
        }
        return lastCursor;
      },
    },
    data: () => {
      return {
        currentPage: 1, // whatever value, bebecause it will be changed by the 'mounted()' function
        // slug of the current pathology, or false if no filter
        currentPathologySlug: false,
        selectedPathology: false,
        allActive: true,
      }
    },
    watch: {
      // si l'url est modifiée (via router.push(...)) alors on va chercher la nouvelle page
      "$route.query.page": function(page) {
        this.currentPage = page ? Math.max(1, page) : 1;
      },
      "$route.params.pathologie": function(pathologieSlug) {
        this.currentPathologySlug = pathologieSlug;
      }
    },
    methods: {
      /**
       * The component Dropdown will emit an event when a pathology is selected.
       * This listener has to manage this event.
       *
       * @param pathologySlug
       */
        pathologySelectionListener(pathologySlug) {
          this.$router.push({params: { pathologie: pathologySlug } });
          this.selectedPathology = pathologySlug;
          this.allActive = false;
        },
        setAllActive() {
            this.allActive = !this.allActive;
            this.selectedPathology = false;
        },
    },
    mounted() {
      // au chargement d'une page, on recupere le num de page courante
      this.currentPage = this.$route.query.page ?   Math.max(1, this.$route.query.page) : 1;

      // et la pathologie courante, si jamais il y a un filtre
      this.currentPathologySlug = this.$route.params.pathologie;
    }
  }
</script>

<style lang="scss">
    .grid-layout {
        padding-left: 0;
        padding-right: 0;
        margin-left: auto;
        margin-right: auto;
        max-width: 1440px;

        .grid-top {
            padding-bottom: 0;
            border: none;

            .col-5 {
                padding: 0;
            }

            .filters {
                display: flex;
                align-items: center;

                .all {
                    font-size: rem(14px);
                    border-radius: rem(24px);
                    font-weight: 600;
                    padding: rem(12px) rem(28px) rem(10px);
                    margin-right: rem(10px);

                    @include mq($until: MD) {
                        padding: rem(9px) rem(13px) rem(7px);
                        margin-right: rem(5px);
                    }

                    &.orange.active {
                        background-color: color(orange);
                        color: white;
                    }

                    &.blue.active {
                        background-color: color(blue);
                        color: white;
                    }

                    &.orange {
                        background-color: white;
                        color: color(orange);
                        border: 1px solid color(border);
                    }

                    &.blue {
                        background-color: white;
                        color: color(blue);
                        border: 1px solid color(border);
                    }
                }
            }

            @include mq($until: MD) {
                .pagination {
                    display: none;
                }
            }
        }

        .card-list {
            display: flex;
            flex-wrap: wrap;

            .card-simple-actu, .card-study {
                max-width: rem(370px);
                margin-bottom: rem(30px);
                width: 100%;

                @include mq($from: MD) {
                    min-width: rem(360px);
                    margin-right: rem(16px);
                }
            }

            @include mq($until: MD) {
                margin: 0 rem(10px);
            }
        }

        .pagination-bottom .pagination {
            justify-content: center;
            margin: 0;
            padding-bottom: rem(40px);
        }
    }
</style>